/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, IForm, SortDirection } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { Toaster } from 'app/shared/lib/toaster';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { CareTeamComments } from 'app/cm/components/case/care-team/care-team-comments';
import { I18N } from 'aurelia-i18n';
import { validate } from 'app/shared/elements/ess-rule';
import * as CM from 'app/cm/models/essette-api';

export interface CustomCareTeamParticipant extends CM.CareTeamParticipant {
    name?: string | null;
    displayName?: string | null;
}
@autoinject()
export class CareTeamGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected showRouteLink: boolean = true;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected rememberExpand: boolean = true;

    protected includeInactive: boolean = false;
    protected careTeamParticipants: CustomCareTeamParticipant[] = [];
    protected allCareTeamParticipants: CustomCareTeamParticipant[] = [];
    protected selectableCareTeamParticipants: CustomCareTeamParticipant[] = [];
    protected memberId: string | null = null;

    protected canEditCareTeam: boolean = false;
    protected canViewComments: boolean = false;

    private sorting: Sorting = new Sorting();
    private defaultSorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected validate = validate;
    protected isValid: boolean = false;

    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CM.CareTeamParticipant[]>;
    protected $pager!: EssPagerCustomElement;
    protected $careTeamModal!: IPopup;
    protected $addToCareTeamForm!: IForm;
    protected $careTeamComments!: CareTeamComments;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        private context: CasePageContext,
        ...rest: any[]) {
        super(...rest);
        // Default sort on page load.
        this.defaultSorting.add({
            reference: '$sort.assignedToFullName',
            direction: SortDirection.Ascending
        });
    }

    protected bind(): void {
        this.checkPermissions();
        this.reload();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            await this.accountContext.load();
            const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
            this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
            this.reload();
        };
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        await this.loadCareTeam();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        this.toaster.standard.saved('cm:careTeam.$', true);
        this.$careTeamModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.readonly = this.readonly || this.context.caseIsReadOnly();
        this.canEditCareTeam = this.accountContext.hasPermission('CM.CareTeamParticipant.Insert.EditCareTeam');
        this.canViewComments = this.accountContext.hasPermission('CM.CareTeamComment.Insert.CareTeamComments');
    }

    public async reload(): Promise<void> {
        if(this.context && this.context.isCaseLoaded && this.context.case.member?.id) {
            this.memberId = this.context.case.member.id;
            await this.loadCareTeam();
        } else {
            await Promise.all([
                this.loadMemberIdByCase(),
                this.loadCareTeam()
            ]);
        }
        if(this.$careTeamComments) this.$careTeamComments.checkUserIsOnCareTeam();
    }

    protected addToCareTeam(): void {
        this.$careTeamModal.open();
    }

    protected careTeamModalClose(): void {
        this.$careTeamModal.close();
    }

    protected buildCreatedByMessage(goal: CM.CarePlanGoal): string | null {
        const stamp = goal.metadata?.created?.timestamp;
        const byUser = goal.metadata?.created?.byUser?.description;

        if(goal.metadata?.triggeredBy?.description) {
            const key = 'ui:stamps.triggeredBy.timestamp';
            const source = goal.metadata.triggeredBy.description;
            return this.i18n.tr(key, { source: source, timestamp: stamp, user: byUser });
        }
        else {
            const key = 'ui:stamps.created.timestamp';
            return this.i18n.tr(key, { timestamp: stamp, user: byUser });
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCareTeam(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        }

        const allOptions: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: true,
            sorting: this.defaultSorting
        }

        const includes: string[] = [
            'assigned.toProvider',
            'assigned.toUser',
            'assigned.toUser.login',
            'case.assignedTo',
            'metadata.assigned.byUser',
            'metadata.activeChanged.byUser',
            'relationship'
        ];

        const fields: string[] = [
            'assigned.toUser.login.loginName',
            'assigned.toProvider.name.full',
            'assigned.toUser.name.full',
            'isActive',
            'metadata.activeChanged.timestamp',
            'metadata.activeChanged.byUser.name.full',
            'metadata.assigned.byUser.description',
            'metadata.assigned.timestamp',
            'relationship.description'
        ];

        const allIncludes: string[] = [
            'assigned.toProvider',
            'assigned.toUser'
        ]

        const allFields: string[] = [
            'assigned.toProvider.name.full',
            'assigned.toProvider.id',
            'assigned.toUser.name.full',
            'assigned.toUser.id'
        ]

        try {
            this.state.beginLoad();

            const [careTeamResponse, allCareTeamResponse] = await Promise.all(
                [
                    this.service.loadCaseCareTeam(this.caseId, options, includes, fields),
                    !this.readonly ? this.service.loadCaseCareTeam(this.caseId, allOptions, allIncludes, allFields) : null
                ]
            );

            this.careTeamParticipants = careTeamResponse.results;
            // For add-to-care-team component to remove participants already in the care team
            this.allCareTeamParticipants = allCareTeamResponse?.results || [];

            // Set up team member name and display names
            this.careTeamParticipants.forEach(participant => {
                participant.name = participant?.assigned?.toUser?.name?.full || participant?.assigned?.toProvider?.name?.full;

                let userLoginName = participant?.assigned?.toUser?.name?.full;

                if(!!participant?.assigned?.toUser?.login?.loginName) {
                    userLoginName += ` (${participant.assigned.toUser.login.loginName})`;
                }

                participant.displayName = userLoginName || participant.name;
            });
            this.allCareTeamParticipants.forEach(participant => {
                participant.name = participant?.assigned?.toUser?.name?.full || participant?.assigned?.toProvider?.name?.full;
            });

            // Filter out providers for selectable participants
            this.selectableCareTeamParticipants = this.allCareTeamParticipants.filter(participant => !participant.assigned?.toProvider);

            this.resultCount = careTeamResponse.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateCareTeamParticipant(careTeamParticipant: CustomCareTeamParticipant, isActive: boolean): Promise<void> {
        if(!careTeamParticipant.id) return;

        if(!isActive) {
            const message: string = this.i18n.tr('ui:messages.actions.remove', { who: '$t(cm:careTeam.participant.$)', what: '$t(cm:careTeam.$)' });
            const result = await confirmation.showCustom(message, null, confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            await this.service.activateCareTeamParticipant(careTeamParticipant.id, isActive);
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall //Loads the case to get the member ID
    private async loadMemberIdByCase(): Promise<void> {
        if(!this.caseId) { return }

        const includes: string[] = [];
        const fields: string[] = ['member.id'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCase(this.caseId, includes, fields);
            this.memberId = response.results[0].member?.id || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
